import { useCallback, useMemo, useState } from 'react';

import { UserInput } from '../shared/UserInput';
import { userToQuery, UserType } from './UserType';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { useGiftApi } from '../hooks/useGiftApi';

interface CopyClaimedGiftsProps {
    sourceUser: UserType;
}

export const CopyClaimedGiftsButton = ({
    sourceUser,
}: CopyClaimedGiftsProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<UserType | null>(null);
    const [copyStatus, setCopyStatus] = useState<
        'idle' | 'loading' | 'success' | 'error'
    >('idle');

    const giftApi = useGiftApi();

    const sourceLegacyClaimsQuery = giftApi.useQuery(
        'get',
        '/gift/api/v1/admin/gifts/claim',
        {
            params: {
                query: userToQuery(sourceUser),
            },
        },
        {
            enabled: !!selectedUser,
        },
    );
    const sourceV2ClaimsQuery = giftApi.useQuery(
        'get',
        '/gift/api/v2/admin/gifts/claim',
        {
            params: {
                query: userToQuery(sourceUser),
            },
        },
        {
            enabled: !!selectedUser,
        },
    );

    const targetLegacyClaimsQuery = giftApi.useQuery(
        'get',
        '/gift/api/v1/admin/gifts/claim',
        {
            params: {
                query: selectedUser ? userToQuery(selectedUser) : undefined,
            },
        },
        {
            enabled: !!selectedUser,
        },
    );
    const targetV2ClaimsQuery = giftApi.useQuery(
        'get',
        '/gift/api/v2/admin/gifts/claim',
        {
            params: {
                query: selectedUser ? userToQuery(selectedUser) : undefined,
            },
        },
        {
            enabled: !!selectedUser,
        },
    );

    const updateLegacyClaimsMutation = giftApi.useMutation(
        'put',
        '/gift/api/v1/admin/gifts/claim',
    );
    const updateV2ClaimsMutation = giftApi.useMutation(
        'put',
        '/gift/api/v2/admin/gifts/claim',
    );

    const sourceLegacyClaimedGiftIds = useMemo(
        () =>
            sourceLegacyClaimsQuery.data?.gifts.map((gift) => gift.giftId) ??
            [],
        [sourceLegacyClaimsQuery.data?.gifts],
    );
    const sourceV2ClaimedGiftIds = useMemo(
        () => sourceV2ClaimsQuery.data?.gifts.map((gift) => gift.id) ?? [],
        [sourceV2ClaimsQuery.data?.gifts],
    );

    const targetLegacyClaimedGiftIds = useMemo(() => {
        if (!targetLegacyClaimsQuery.data) return new Set<string>();
        return new Set(
            targetLegacyClaimsQuery.data.gifts.map((gift) => gift.giftId),
        );
    }, [targetLegacyClaimsQuery.data]);
    const targetV2ClaimedGiftIds = useMemo(() => {
        if (!targetV2ClaimsQuery.data) return new Set<string>();
        return new Set(targetV2ClaimsQuery.data.gifts.map((gift) => gift.id));
    }, [targetV2ClaimsQuery.data]);

    const newLegacyGiftsToAdd = useMemo(() => {
        if (!selectedUser) return [];
        return sourceLegacyClaimedGiftIds.filter(
            (id) => !targetLegacyClaimedGiftIds.has(id),
        );
    }, [sourceLegacyClaimedGiftIds, targetLegacyClaimedGiftIds, selectedUser]);
    const newV2GiftsToAdd = useMemo(() => {
        if (!selectedUser) return [];
        return sourceV2ClaimedGiftIds.filter(
            (id) => !targetV2ClaimedGiftIds.has(id),
        );
    }, [selectedUser, sourceV2ClaimedGiftIds, targetV2ClaimedGiftIds]);
    const sourceGiftsCount =
        sourceLegacyClaimedGiftIds.length + sourceV2ClaimedGiftIds.length;
    const giftsToAddCount = newLegacyGiftsToAdd.length + newV2GiftsToAdd.length;

    const handleUserSelected = useCallback((user: UserType | null) => {
        setCopyStatus('idle');
        setSelectedUser(user);
    }, []);

    const handleStartCopy = useCallback(() => {
        if (!selectedUser || giftsToAddCount === 0) return;
        setCopyStatus('loading');

        const mergedLegacyGiftIds = [
            ...Array.from(targetLegacyClaimedGiftIds),
            ...newLegacyGiftsToAdd,
        ];
        const mergedV2GiftIds = [
            ...Array.from(targetV2ClaimedGiftIds),
            ...newV2GiftsToAdd,
        ];

        async function performMutation() {
            if (!selectedUser || giftsToAddCount === 0) return;

            try {
                await updateLegacyClaimsMutation.mutateAsync({
                    body: {
                        user: selectedUser,
                        giftIds: mergedLegacyGiftIds,
                    },
                });
                await updateV2ClaimsMutation.mutateAsync({
                    body: {
                        user: selectedUser,
                        giftIds: mergedV2GiftIds,
                    },
                });
                setCopyStatus('success');
            } catch (e) {
                console.error('Failed to copy gift claims', { error: e });
                setCopyStatus('error');
            }
        }
        performMutation();
    }, [
        selectedUser,
        giftsToAddCount,
        targetLegacyClaimedGiftIds,
        newLegacyGiftsToAdd,
        targetV2ClaimedGiftIds,
        newV2GiftsToAdd,
        updateLegacyClaimsMutation,
        updateV2ClaimsMutation,
    ]);

    const handleClose = () => {
        setIsOpen(false);
        setSelectedUser(null);
        setCopyStatus('idle');
    };

    const isSearching = targetLegacyClaimsQuery.isFetching;
    const hasSearched =
        !!selectedUser && !isSearching && targetLegacyClaimsQuery.isSuccess;
    const hasError = targetLegacyClaimsQuery.isError;

    return (
        <>
            <Button
                variant="outlined"
                onClick={() => setIsOpen(true)}
                disabled={sourceGiftsCount === 0}
            >
                Copy Claimed Gifts
            </Button>

            <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>Copy Claimed Gifts</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <UserInput onUserSelected={handleUserSelected} />

                        {isSearching && (
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    py: 2,
                                }}
                            >
                                <CircularProgress size={24} />
                            </Box>
                        )}

                        {hasError && (
                            <Alert severity="error">
                                User not found or error fetching claims.
                            </Alert>
                        )}

                        {hasSearched && (
                            <>
                                {giftsToAddCount === 0 ? (
                                    <Alert severity="info">
                                        The target profile already has all
                                        claimed gifts. Nothing to copy.
                                    </Alert>
                                ) : (
                                    <>
                                        <Typography variant="body2">
                                            {newLegacyGiftsToAdd.length} legacy
                                            gift(s) to copy:
                                        </Typography>
                                        <List
                                            dense
                                            sx={{
                                                maxHeight: 200,
                                                overflow: 'auto',
                                            }}
                                        >
                                            {newLegacyGiftsToAdd.map(
                                                (giftId) => (
                                                    <ListItem key={giftId}>
                                                        <ListItemText
                                                            primary={giftId}
                                                        />
                                                    </ListItem>
                                                ),
                                            )}
                                        </List>

                                        <Typography variant="body2">
                                            {newV2GiftsToAdd.length} v2 gift(s)
                                            to copy:
                                        </Typography>
                                        <List
                                            dense
                                            sx={{
                                                maxHeight: 200,
                                                overflow: 'auto',
                                            }}
                                        >
                                            {newV2GiftsToAdd.map((giftId) => (
                                                <ListItem key={giftId}>
                                                    <ListItemText
                                                        primary={giftId}
                                                    />
                                                </ListItem>
                                            ))}
                                        </List>
                                    </>
                                )}
                            </>
                        )}

                        {copyStatus === 'success' && (
                            <Alert severity="success">
                                Successfully copied {giftsToAddCount} gift(s) to
                                target user.
                            </Alert>
                        )}

                        {copyStatus === 'error' && (
                            <Alert severity="error">
                                Error copying gifts. Please try again.
                            </Alert>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleStartCopy}
                        disabled={
                            !hasSearched ||
                            giftsToAddCount === 0 ||
                            copyStatus === 'loading' ||
                            copyStatus === 'success'
                        }
                    >
                        {copyStatus === 'loading' ? (
                            <CircularProgress size={20} />
                        ) : (
                            'Start Copy'
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};
